import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Plus, CheckCircle, Trash2, ShieldCheck, Edit3, Save, X , ArrowLeft} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import apiService from '../../components/services/apiService';

export default function ProfilePage() {
  const { user, setUser } = useContext(AuthContext);
  const { showToast } = useToast();
  const navigate = useNavigate();
  // State chỉnh sửa thông tin cá nhân
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [fullName, setFullName] = useState(user?.fullName || user?.name || '');
  const [phoneNumber, setPhone] = useState(user?.phoneNumber || '');

  // State quản lý sổ địa chỉ
  const [addresses, setAddresses] = useState([]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddr, setNewAddr] = useState({
    recipientName: '',
    streetAddress: '',
    phone: '',
    city: '',
    isDefault: false
  });

  // Gọi API lấy danh sách địa chỉ của user khi vào trang
  useEffect(() => {
    if (user) {
      fetchAddresses();
    }
  }, [user]);

  
  const fetchAddresses = async () => {
    try {
  
      const res = await apiService.get("/users/addresses");
      setAddresses(res.data);
    } catch (error) {
      console.error("Lỗi tải danh sách địa chỉ:", error);
    }
  };

  // Xử lý cập nhật thông tin cá nhân
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await apiService.put("/users/profile", 
        { fullName, phoneNumber }
      );
      
      // Lấy dữ liệu trả về từ backend
      const responseData = res.data.data || res.data;

      // SỬA LẠI: Dùng ...user để trải phẳng object user cũ
      const updatedUser = {
        ...user, 
        fullName: responseData.fullName || fullName,
        phoneNumber: responseData.phoneNumber || phoneNumber
      };
      
      console.log("Updated User:", updatedUser);

      // Cập nhật lại state user chung và localStorage
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      showToast("Cập nhật thông tin thành công! 🎉", "success");
      setIsEditingInfo(false);
    } catch (error) {
      console.error("Lỗi cập nhật:", error);
      showToast("Không thể cập nhật thông tin", "error");
    }
  };

  // Xử lý thêm địa chỉ mới
  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      await apiService.post("/users/addresses", newAddr);
      showToast("Thêm địa chỉ giao hàng thành công! 📦", "success");
      setShowAddAddress(false);
      setNewAddr({ recipientName: '', streetAddress: '', phone: '', city: '', isDefault: false });
      fetchAddresses();
    } catch (error) {
      showToast("Không thể thêm địa chỉ mới", "error");
    }
  };

  // Xử lý xóa địa chỉ
  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Bạn có chắc muốn xóa địa chỉ này không?")) return;
    try {
      await apiService.delete(`/users/addresses/${id}`);
      showToast("Đã xóa địa chỉ", "success");
      fetchAddresses();
    } catch (error) {
      showToast("Không thể xóa địa chỉ", "error");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      <div>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white hover:bg-slate-50 border border-slate-200 px-3.5 py-2 rounded-xl transition shadow-sm cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Quay lại trang chủ
        </button>
      </div>
      {/* Tiêu đề trang */}
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Hồ Sơ Của Tôi</h1>
        <p className="text-xs text-slate-500 mt-1">Quản lý thông tin tài khoản và bảo mật địa chỉ nhận hàng JapanICHIBA</p>
      </div>

      {/* 1. Thẻ thông tin cá nhân */}
      <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm relative">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold text-2xl shadow-inner">
              {user?.fullName ? user.fullName.charAt(0).toUpperCase() : <User className="w-8 h-8" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{user?.fullName || user?.name || "Khách hàng"}</h2>
              <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <Mail className="w-3.5 h-3.5 text-indigo-500" /> {user?.email}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1 border border-emerald-200">
                  <ShieldCheck className="w-3 h-3" /> Tài khoản hoạt động
                </span>
                <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">
                  {user?.role || "CUSTOMER"}
                </span>
              </div>
            </div>
          </div>

          {!isEditingInfo && (
            <button
              onClick={() => setIsEditingInfo(true)}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-4 py-2 rounded-xl text-xs transition flex items-center gap-1.5 cursor-pointer"
            >
              <Edit3 className="w-3.5 h-3.5" /> Chỉnh sửa thông tin
            </button>
          )}
        </div>

        {/* Form chỉnh sửa thông tin */}
        {isEditingInfo ? (
          <form onSubmit={handleUpdateProfile} className="mt-6 space-y-4 max-w-lg">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Họ và tên</label>
              <input 
                type="text" 
                value={fullName} 
                onChange={(e) => setFullName(e.target.value)} 
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium"
                required 
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Số điện thoại cá nhân</label>
              <input 
                type="text" 
                value={phoneNumber} 
                onChange={(e) => setPhone(e.target.value)} 
                placeholder="Nhập số điện thoại..." 
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium" 
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button type="submit" className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-xl text-xs transition flex items-center gap-1 cursor-pointer">
                <Save className="w-3.5 h-3.5" /> Lưu thay đổi
              </button>
              <button type="button" onClick={() => setIsEditingInfo(false)} className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold px-4 py-2 rounded-xl text-xs transition cursor-pointer">
                Hủy
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-600">
            <div><span className="text-slate-400 font-semibold">Số điện thoại:</span> {user?.phoneNumber || "Chưa cập nhật"}</div>
            <div><span className="text-slate-400 font-semibold">Mã định danh (ID):</span> #{user?.id}</div>
          </div>
        )}
      </div>

      {/* 2. Sổ địa chỉ nhận hàng */}
      <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" /> Sổ Địa Chỉ Giao Hàng ({addresses.length})
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Quản lý các địa điểm nhận hàng tại Việt Nam cho đơn hàng Nhật</p>
          </div>
          <button
            onClick={() => setShowAddAddress(!showAddAddress)}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-xl text-xs transition flex items-center gap-1.5 shadow-md cursor-pointer"
          >
            <Plus className="w-4 h-4" /> Thêm địa chỉ mới
          </button>
        </div>

        {/* Form thêm địa chỉ mới */}
        {showAddAddress && (
          <form onSubmit={handleAddAddress} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 mb-6 space-y-4">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Thêm địa chỉ giao hàng mới</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input 
                type="text" 
                placeholder="Tên người nhận hàng" 
                value={newAddr.recipientName} 
                onChange={(e) => setNewAddr({...newAddr, recipientName: e.target.value})} 
                className="p-3 bg-white border border-slate-200 rounded-xl text-xs" 
                required 
              />
              <input 
                type="text" 
                placeholder="Số điện thoại nhận hàng" 
                value={newAddr.phone} 
                onChange={(e) => setNewAddr({...newAddr, phone: e.target.value})} 
                className="p-3 bg-white border border-slate-200 rounded-xl text-xs" 
                required 
              />
              <input 
                type="text" 
                placeholder="Tỉnh / Thành phố (Ví dụ: Hồ Chí Minh, Hà Nội...)" 
                value={newAddr.city} 
                onChange={(e) => setNewAddr({...newAddr, city: e.target.value})} 
                className="p-3 bg-white border border-slate-200 rounded-xl text-xs" 
                required 
              />
              <input 
                type="text" 
                placeholder="Địa chỉ chi tiết (Số nhà, tên đường, phường/xã...)" 
                value={newAddr.streetAddress} 
                onChange={(e) => setNewAddr({...newAddr, streetAddress: e.target.value})} 
                className="p-3 bg-white border border-slate-200 rounded-xl text-xs sm:col-span-2" 
                required 
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowAddAddress(false)} className="px-4 py-2 bg-slate-200 text-slate-700 font-bold rounded-xl text-xs cursor-pointer">Hủy</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-xs shadow cursor-pointer">Lưu địa chỉ</button>
            </div>
          </form>
        )}

        {/* Danh sách địa chỉ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((addr) => (
            <div key={addr.id} className={`p-4 rounded-2xl border transition relative flex flex-col justify-between ${addr.isDefault ? 'border-indigo-600 bg-indigo-50/20 shadow-sm' : 'border-slate-200 bg-white'}`}>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-slate-900 text-sm">{addr.recipientName}</span>
                  <button onClick={() => handleDeleteAddress(addr.id)} className="text-slate-400 hover:text-rose-600 transition p-1 cursor-pointer" title="Xóa địa chỉ">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 font-semibold mb-2">📞 {addr.phone}</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  🏠 {addr.streetAddress}, {addr.city}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                {addr.isDefault ? (
                  <span className="bg-indigo-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Mặc định
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400">Địa chỉ phụ</span>
                )}
              </div>
            </div>
          ))}
          {addresses.length === 0 && (
            <div className="col-span-2 text-center py-8 text-slate-400 text-xs border border-dashed border-slate-200 rounded-2xl">
              Chưa có địa chỉ giao hàng nào được lưu. Hãy thêm địa chỉ để thuận tiện mua sắm!
            </div>
          )}
        </div>

      </div>

    </div>
  );
}